import { useEffect, useRef } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { OPEN_ATTRIBUTE, TaskChipButton } from "./TaskChipButton";
import {
    TaskFieldControl,
    type TaskFieldEditor,
} from "./TaskBoardFieldControls";
import {
    TASK_CARD_FIELD_LABELS,
    type TaskCardEditField,
} from "./taskCardEdits";
import styles from "./TaskBoardChips.module.scss";

interface TaskBoardShortcutAnchorProps {
    field: TaskCardEditField;
    editor: TaskFieldEditor;
    /** Called once the control closes. */
    onEnd: () => void;
}

/**
 * Opens `field`'s control from a card shortcut or the Add field menu: an
 * unseen trigger over its positioned parent (the whole card, or the Add
 * field chip), clicked once on mount. Closing the control hands focus back
 * to its trigger, which the card takes, then ends.
 */
export const TaskBoardShortcutAnchor = ({
    field,
    editor,
    onEnd,
}: Readonly<TaskBoardShortcutAnchorProps>) => {
    const anchorRef = useRef<HTMLSpanElement>(null);
    const hasClickedRef = useRef(false);
    const onEndRef = useLatestRef(onEnd);

    useEffect(() => {
        const trigger = anchorRef.current?.querySelector("button");
        if (!trigger) {
            onEndRef.current();
            return undefined;
        }
        let hasOpened = trigger.hasAttribute(OPEN_ATTRIBUTE);
        const observer = new MutationObserver(() => {
            if (trigger.hasAttribute(OPEN_ATTRIBUTE)) {
                hasOpened = true;
            } else if (hasOpened) {
                onEndRef.current();
            }
        });
        observer.observe(trigger, { attributeFilter: [OPEN_ATTRIBUTE] });
        if (!hasClickedRef.current) {
            hasClickedRef.current = true;
            trigger.click();
        }
        return () => observer.disconnect();
    }, [onEndRef]);

    return (
        <span ref={anchorRef} className={styles.shortcutAnchor}>
            <TaskFieldControl
                field={field}
                editor={editor}
                renderTrigger={(props) => (
                    <TaskChipButton
                        {...props}
                        aria-label={TASK_CARD_FIELD_LABELS[field]}
                        className={styles.shortcutTrigger}
                    />
                )}
            />
        </span>
    );
};
